import { useState } from 'react';
import axios from 'axios';
import classNames from 'classnames';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import Heading from '../components/Heading';
import PrimaryButton from '../components/buttons/PrimaryButton';
import Http from '../services/http';
import { useNotifications } from '../components/notifications/useNotifications';

interface ApiToken {
  uuid: string;
  client_id: string;
  allowed_client_codes: string[];
  is_active: boolean;
  description: string;
  created_at: string;
  token?: string;
}

interface EndpointParam {
  name: string;
  desc: string;
}

interface EndpointDoc {
  method: string;
  path: string;
  description: string;
  variant: 'list' | 'single';
  params?: EndpointParam[];
  examplePath: string;
}

const COMMON_LIST_PARAMS: EndpointParam[] = [
  { name: 'page', desc: 'Page number (default: 1)' },
  { name: 'per_page', desc: 'Results per page (default: 50, max: 100)' },
  { name: 'sort_by', desc: 'Field to sort by (default: created_at)' },
  { name: 'sort_direction', desc: 'asc or desc (default: desc)' },
];

const ENDPOINTS: EndpointDoc[] = [
  {
    method: 'GET',
    path: '/broker',
    description: 'List broker entries (paginated)',
    variant: 'list',
    params: [
      ...COMMON_LIST_PARAMS,
      { name: 'part_number', desc: 'Filter by part number' },
      { name: 'hts', desc: 'Filter by HTS code' },
      { name: 'supplier_id', desc: 'Filter by supplier ID' },
      { name: 'country_of_origin', desc: 'Filter by country' },
    ],
    examplePath: '/api/v1/broker?page=1&per_page=10',
  },
  {
    method: 'GET',
    path: '/broker/:uuid',
    description: 'Get single broker entry by UUID',
    variant: 'single',
    examplePath: '/api/v1/broker/UUID_HERE',
  },
  {
    method: 'GET',
    path: '/hts',
    description: 'List HTS classification entries (paginated)',
    variant: 'list',
    params: [
      ...COMMON_LIST_PARAMS,
      {
        name: 'part_number',
        desc: 'Filter by part number (comma-separated for multiple)',
      },
      { name: 'hts_number', desc: 'Filter by HTS number' },
      { name: 'description', desc: 'Filter by description' },
      { name: 'country_hts', desc: 'Filter by country HTS' },
      {
        name: 'confidence_level',
        desc: 'Filter by confidence level (exact match)',
      },
      {
        name: 'date_classified_start',
        desc: 'Classified on or after this date (ISO 8601)',
      },
      {
        name: 'date_classified_end',
        desc: 'Classified on or before this date (ISO 8601)',
      },
    ],
    examplePath: '/api/v1/hts?page=1&per_page=10',
  },
  {
    method: 'GET',
    path: '/hts/:uuid',
    description: 'Get single HTS entry by UUID',
    variant: 'single',
    examplePath: '/api/v1/hts/UUID_HERE',
  },
  {
    method: 'GET',
    path: '/fta-supplier',
    description: 'List FTA supplier solicitation entries (paginated)',
    variant: 'list',
    params: [
      ...COMMON_LIST_PARAMS,
      {
        name: 'part_number',
        desc: 'Filter by part number (comma-separated for multiple)',
      },
      { name: 'supplier_name', desc: 'Filter by supplier name' },
      { name: 'program', desc: 'Filter by program (exact match)' },
      { name: 'cert_number', desc: 'Filter by certificate number' },
      { name: 'status', desc: 'Filter by status (exact match)' },
      {
        name: 'blanket_start',
        desc: 'Blanket start on or after this date (ISO 8601)',
      },
      {
        name: 'blanket_end',
        desc: 'Blanket end on or before this date (ISO 8601)',
      },
    ],
    examplePath: '/api/v1/fta-supplier?page=1&per_page=10',
  },
  {
    method: 'GET',
    path: '/fta-supplier/:uuid',
    description: 'Get single FTA supplier entry by UUID',
    variant: 'single',
    examplePath: '/api/v1/fta-supplier/UUID_HERE',
  },
  {
    method: 'GET',
    path: '/fta-issued',
    description: 'List FTA issued certificate entries (paginated)',
    variant: 'list',
    params: [
      ...COMMON_LIST_PARAMS,
      {
        name: 'part_number',
        desc: 'Filter by part number (comma-separated for multiple)',
      },
      { name: 'customer_name', desc: 'Filter by customer name' },
      { name: 'program', desc: 'Filter by program (exact match)' },
      { name: 'cert_number', desc: 'Filter by certificate number' },
      { name: 'status', desc: 'Filter by status (exact match)' },
      {
        name: 'blanket_start',
        desc: 'Blanket start on or after this date (ISO 8601)',
      },
      {
        name: 'blanket_end',
        desc: 'Blanket end on or before this date (ISO 8601)',
      },
    ],
    examplePath: '/api/v1/fta-issued?page=1&per_page=10',
  },
  {
    method: 'GET',
    path: '/fta-issued/:uuid',
    description: 'Get single FTA issued entry by UUID',
    variant: 'single',
    examplePath: '/api/v1/fta-issued/UUID_HERE',
  },
];

const ApiAccess = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const { pushNotification } = useNotifications();

  const handleGenerateToken = async () => {
    setIsLoading(true);
    setGeneratedToken(null);

    try {
      const response = await axios.post<{ token: string; record: ApiToken }>(
        `${process.env.REACT_APP_API}/api-token/generate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Http.accessToken}`,
          },
        }
      );

      setGeneratedToken(response.data.token);

      pushNotification({
        type: 'success',
        heading: 'API token generated successfully',
      });
    } catch (error) {
      console.error('Error generating token:', error);

      pushNotification({
        type: 'error',
        heading: 'Failed to generate API token',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);

    pushNotification({
      type: 'success',
      heading: 'Token copied to clipboard',
    });
  };

  return (
    <>
      <Heading title="API Access" showFilter={false} />

      <div className="bg-white shadow rounded-lg p-6 mt-6">
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            Generate API Token
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Create a new API token to access the FOCUS Portal API
            programmatically.
          </p>

          <PrimaryButton
            onClick={handleGenerateToken}
            disabled={isLoading}
            color="green"
          >
            {isLoading ? 'Generating...' : 'Generate New Token'}
          </PrimaryButton>
        </div>

        {generatedToken && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Save this token now!
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p className="mb-2">
                    This token will only be shown once. Make sure to copy it
                    now.
                  </p>
                  <div className="bg-white p-3 rounded border border-yellow-200 font-mono text-xs break-all">
                    {generatedToken}
                  </div>
                  <button
                    onClick={() => copyToClipboard(generatedToken)}
                    className="mt-2 text-sm text-yellow-800 underline hover:text-yellow-900"
                  >
                    Copy to clipboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            API Documentation
          </h2>

          <div className="space-y-4 text-sm text-gray-700">
            <div>
              <h3 className="font-semibold mb-2">Authentication</h3>
              <p className="mb-2">
                All API requests must include your token in the Authorization
                header:
              </p>
              <pre className="bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto">
                <code>Authorization: Bearer YOUR_TOKEN_HERE</code>
              </pre>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Base URL</h3>
              <pre className="bg-gray-50 p-3 rounded border border-gray-200">
                <code>{process.env.REACT_APP_API}/api/v1</code>
              </pre>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Endpoints</h3>

              <div className="space-y-2">
                {ENDPOINTS.map((ep) => {
                  const isList = ep.variant === 'list';
                  const borderColor = isList
                    ? 'border-blue-500'
                    : 'border-green-500';
                  const textColor = isList ? 'text-blue-900' : 'text-green-900';

                  return (
                    <Disclosure key={`${ep.method} ${ep.path}`}>
                      {({ open }) => (
                        <div
                          className={classNames(
                            'border-l-4 pl-4 rounded-r bg-gray-50/50',
                            borderColor
                          )}
                        >
                          <Disclosure.Button className="flex w-full items-center justify-between py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-r">
                            <h4
                              className={classNames(
                                'font-semibold font-mono text-sm',
                                textColor
                              )}
                            >
                              {ep.method} {ep.path}
                            </h4>
                            <ChevronDownIcon
                              className={classNames(
                                'h-5 w-5 text-gray-400 transition-transform duration-150',
                                { 'rotate-180': open }
                              )}
                              aria-hidden="true"
                            />
                          </Disclosure.Button>

                          <Disclosure.Panel className="pb-3">
                            <p className="text-gray-600 mb-2">
                              {ep.description}
                            </p>

                            {ep.params && (
                              <div className="mt-2">
                                <p className="font-medium text-xs text-gray-500 mb-1">
                                  Query Parameters:
                                </p>
                                <ul className="list-disc list-inside text-xs space-y-1 text-gray-600">
                                  {ep.params.map((param) => (
                                    <li key={param.name}>
                                      <code>{param.name}</code> - {param.desc}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            <div className="mt-2">
                              <p className="font-medium text-xs text-gray-500 mb-1">
                                Example:
                              </p>
                              <pre className="bg-gray-50 p-2 rounded border border-gray-200 text-xs overflow-x-auto">
                                <code>
                                  curl -X GET "{process.env.REACT_APP_API}
                                  {ep.examplePath}" \{'\n'} -H "Authorization:
                                  Bearer YOUR_TOKEN_HERE"
                                </code>
                              </pre>
                            </div>
                          </Disclosure.Panel>
                        </div>
                      )}
                    </Disclosure>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Response Format</h3>
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500">
                  List response:
                </p>
                <pre className="bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto text-xs">
                  <code>{`{
  "data": [...],
  "pagination": {
    "total": 1000,
    "page": 1,
    "per_page": 50,
    "total_pages": 20
  }
}`}</code>
                </pre>

                <p className="text-xs font-medium text-gray-500 mt-4">
                  Single item response:
                </p>
                <pre className="bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto text-xs">
                  <code>{`{
  "data": {
    "uuid": "...",
    "Part Number": "...",
    ...
  }
}`}</code>
                </pre>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-4">
              <h3 className="font-semibold text-blue-900 mb-2">
                Important Notes
              </h3>
              <ul className="list-disc list-inside text-xs space-y-1 text-blue-800">
                <li>
                  Keep your API tokens secure and never share them publicly
                </li>
                <li>
                  Each token is unique and cannot be recovered once the page is
                  closed
                </li>
                <li>
                  You can only access data for your authorized client codes
                </li>
                <li>The API is read-only - no modifications are allowed</li>
                <li>Rate limiting may apply to prevent abuse</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApiAccess;
